import client from './graphQlClient';
import {
    FIXTURE_RECENT_CHANGES_FOR_DATASET_QUERY,
    VESSEL_FIXTURES_QUERY,
    GET_MAINTAINANCE_MESSAGE_QUERY,
    MAKE_FIXTURE_RUMOURED_MUTATION,
    MAKE_FIXTURE_UNRUMOURED_MUTATION,
    UPDATE_FIXTURE_RUMOURED_FIELDS_MUTATION,
} from './queries/fixtures/FixtureQueries';
import logger from '_legacy/diagnostics/logging/logger';
import { displayOnlyRumouredHandler } from './apiHelpers';
import { snapshotApi } from './snapshotApi';

export class FixtureApi {
    static CONTEXT = {
        errorHandling: {
            unknownResult: {
                resolution: {
                    isSafeToRetry: true,
                },
            },
        },
    };
    static FETCH_POLICY = 'no-cache';

    static fetchFixtures = async (
        groupId,
        datasetId,
        numberOfYears,
        cancelToken,
        includeDeleted,
        displayOnlyRumoured,
        loadReason
    ) => {
        const changesSinceSnapshotPromise = (async () => {
            try {
                const resultPromise = client().query({
                    query: FIXTURE_RECENT_CHANGES_FOR_DATASET_QUERY,
                    variables: { groupId, numberOfYears },
                    fetchPolicy: this.FETCH_POLICY,
                    context: { loadReason },
                });

                const result = await Promise.race([
                    resultPromise,
                    cancelToken.promise,
                ]);

                if (result.reason) {
                    logger.error(
                        'Result changesSinceSnapshotPromise: ',
                        result
                    );
                    throw result;
                }

                return result.data;
            } catch (error) {
                logger.error('Error changesSinceSnapshotPromise: ', error);
                throw error;
            }
        })();

        const snapshotPromise = snapshotApi.fetchFixturesSnapshot({
            datasetId,
            numberOfYears,
        });

        const {
            publicFixturesRecentlyChanged = [],
            groupOnlyFixtures = [],
            fixturesWithPrivateComments = [],
            individualFixtures = [],
            unavailableFixtureIdsFromLastSnapshot = [],
        } = (await changesSinceSnapshotPromise) || {};

        let fixtures = await snapshotPromise;

        const m = new Map();

        fixtures.forEach((i) => m.set(i.id, i));
        unavailableFixtureIdsFromLastSnapshot.forEach((f) => m.delete(f));
        publicFixturesRecentlyChanged.forEach((f) => m.set(f.id, f));
        groupOnlyFixtures.forEach((f) => m.set(f.id, f));
        fixturesWithPrivateComments.forEach((f) => m.set(f.id, f));
        individualFixtures.forEach((f) => m.set(f.id, f));

        displayOnlyRumouredHandler(m, displayOnlyRumoured);

        if (!includeDeleted) {
            m.forEach((f) => {
                if (f.state === 'deleted') {
                    m.delete(f.id);
                }
            });
        }

        fixtures = Array.from(m.values());

        return fixtures;
    };

    static fetchVesselFixtures = async (
        groupId,
        numberOfYears,
        cancelToken,
        includeDeleted,
        vesselImo,
        loadReason
    ) => {
        const vesselFixturesPromise = (async () => {
            try {
                const resultPromise = client().query({
                    query: VESSEL_FIXTURES_QUERY,
                    variables: { groupId, vesselImo, numberOfYears },
                    fetchPolicy: this.FETCH_POLICY,
                    context: { loadReason },
                });

                const result = await Promise.race([
                    resultPromise,
                    cancelToken.promise,
                ]);

                if (result.reason) {
                    logger.error('Result vessel fixture promise: ', result);
                    throw result;
                }

                return result.data;
            } catch (error) {
                logger.error('Error vessel fixture promise: ', error);
                throw error;
            }
        })();

        const { vesselFixtures = [] } = (await vesselFixturesPromise) || {};

        const fixturesMap = new Map();

        vesselFixtures.forEach((f) => fixturesMap.set(f.id, f));

        // displayOnlyRumouredHandler(m, displayOnlyRumoured);

        if (!includeDeleted) {
            fixturesMap.forEach((f) => {
                if (f.state === 'deleted') {
                    fixturesMap.delete(f.id);
                }
            });
        }

        let fixtures = Array.from(fixturesMap.values());

        return fixtures;
    };

    static getMaintainanceMessage = (reportName) => {
        return new Promise((resolve, reject) => {
            client()
                .query({
                    query: GET_MAINTAINANCE_MESSAGE_QUERY,
                    fetchPolicy: this.FETCH_POLICY,
                    variables: {
                        reportName,
                    },
                })
                .then((result) => {
                    resolve(result.data.getAnalyticsMaintainanceMessage);
                })
                .catch((reason) => {
                    reject(reason);
                });
        });
    };

    static makeUnRumoured = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_FIXTURE_UNRUMOURED_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeFixtureUnRumoured);

    static makeRumoured = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_FIXTURE_RUMOURED_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeFixtureRumoured);

    static updateRumouredFields = (client, variables) =>
        client
            .mutate({
                mutation: UPDATE_FIXTURE_RUMOURED_FIELDS_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.updateFixtureRumouredFields);
}
